import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { registerUser, loginUser } from '@/lib/customAuth';
import { useAuth } from '@/hooks/useAuth';

const EDGE_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth';

type Mode = 'login' | 'register' | 'forgot';
type ForgotStep = 'input' | 'done';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, refresh } = useAuth();
  const [mode, setMode] = useState<Mode>('login');

  // 登录字段
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // 注册字段
  const [regNickname, setRegNickname] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regInviteCode, setRegInviteCode] = useState('');

  // 忘记密码字段
  const [forgotStep, setForgotStep] = useState<ForgotStep>('input');
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotNewPwd, setForgotNewPwd] = useState('');
  const [forgotConfirmPwd, setForgotConfirmPwd] = useState('');
  const [showForgotPwd, setShowForgotPwd] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [inviteCodeLocked, setInviteCodeLocked] = useState(false);
  const [frozenNotice, setFrozenNotice] = useState(false);

  const from = (location.state as { from?: string })?.from ?? '/mine';

  useEffect(() => {
    // 注册成功时不自动跳转，等用户看完提示
    if (!loading && user && !registerSuccess) { navigate(from, { replace: true }); }
  }, [user, loading, navigate, from, registerSuccess]);

  // 注册成功后倒计时自动切换到登录
  useEffect(() => {
    if (!registerSuccess) return;
    setCountdown(3);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setRegisterSuccess(false);
          switchMode('login');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [registerSuccess]);

  // 从 URL 参数自动填入邀请码
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('invite') || params.get('code') || params.get('ref');
    if (code) {
      setRegInviteCode(code.toUpperCase());
      setInviteCodeLocked(true);
      setMode('register');
    }
  }, [location.search]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!agreed) { setError('请先同意用户协议和隐私政策'); return; }
    if (!username.trim()) { setError('请输入手机号'); return; }
    if (!password) { setError('请输入密码'); return; }
    setSubmitting(true);
    try {
      const result = await loginUser({ username: username.trim(), password, rememberMe });
      if ('error' in result) { setError(result.error); }
      else {
        if (result.user.status === 'frozen') {
          setFrozenNotice(true);
        }
        refresh();
      }
    } finally { setSubmitting(false); }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!agreed) { setError('请先同意用户协议和隐私政策'); return; }
    if (!regNickname.trim()) { setError('请输入昵称'); return; }
    if (!regUsername.trim()) { setError('请输入用户名'); return; }
    if (!/^[a-zA-Z0-9_\u4e00-\u9fa5]{2,20}$/.test(regUsername.trim())) {
      setError('用户名只能包含中英文、数字、下划线，2-20个字符'); return;
    }
    if (!regPhone.trim() || !/^1[3-9]\d{9}$/.test(regPhone.trim())) {
      setError('请输入正确的手机号'); return;
    }
    if (regPassword.length < 6) { setError('密码至少需要6位'); return; }
    if (regPassword !== regConfirmPassword) { setError('两次输入的密码不一致'); return; }
    setSubmitting(true);
    try {
      const result = await registerUser({
        username: regUsername.trim(),
        password: regPassword,
        nickname: regNickname.trim(),
        phone: regPhone.trim(),
        inviteCode: regInviteCode.trim() || undefined,
      });
      if ('error' in result) { setError(result.error); }
      else { setRegisterSuccess(true); }
    } finally { setSubmitting(false); }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!forgotPhone.trim() || !/^1[3-9]\d{9}$/.test(forgotPhone.trim())) {
      setError('请输入正确的手机号'); return;
    }
    if (forgotNewPwd.length < 6) { setError('新密码至少需要6位'); return; }
    if (forgotNewPwd !== forgotConfirmPwd) { setError('两次输入的密码不一致'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reset_password_by_phone', phone: forgotPhone.trim(), newPassword: forgotNewPwd }),
      });
      const result = await res.json();
      if (result.error) { setError(result.error); }
      else { setForgotStep('done'); }
    } finally { setSubmitting(false); }
  };

  const switchMode = (m: Mode) => { setMode(m); setError(''); setForgotStep('input'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center max-w-[480px] mx-auto">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white max-w-[480px] mx-auto flex flex-col">
      {/* Top bar */}
      <div className="flex items-center px-4 pt-12 pb-4">
        {mode === 'forgot' ? (
          <button onClick={() => switchMode('login')} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
            <i className="ri-arrow-left-line text-gray-700 text-lg" />
          </button>
        ) : (
          <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
            <i className="ri-arrow-left-line text-gray-700 text-lg" />
          </button>
        )}
      </div>

      {/* Hero */}
      <div className="px-6 pt-4 pb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-400 flex items-center justify-center mb-5">
          <i className={`${mode === 'forgot' ? 'ri-lock-password-line' : 'ri-store-2-line'} text-white text-2xl`} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1.5">
          {mode === 'login' ? '欢迎回来' : mode === 'register' ? '创建账户' : '重置密码'}
        </h1>
        <p className="text-sm text-gray-400">
          {mode === 'login' ? '登录后享受完整购物体验'
            : mode === 'register' ? '注册成为会员，开启分销之旅'
            : '输入注册手机号和新密码，直接重置登录密码'}
        </p>
      </div>

      {/* Tab switcher — 仅登录/注册显示 */}
      {mode !== 'forgot' && !registerSuccess && (
        <div className="mx-6 mb-6 flex bg-gray-100 rounded-full p-1">
          {(['login', 'register'] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => switchMode(m)}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
              }`}
            >
              {m === 'login' ? '登录' : '注册'}
            </button>
          ))}
        </div>
      )}

      {/* ── 登录表单 ── */}
      {mode === 'login' && (
        <form onSubmit={handleLogin} className="px-6 flex-1 flex flex-col gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">手机号 <span className="text-rose-500">*</span></label>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-smartphone-line text-gray-400 text-base" />
              </div>
              <input
                type="tel"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="请输入手机号"
                maxLength={11}
                className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs text-gray-500 font-medium">密码 <span className="text-rose-500">*</span></label>
              <button type="button" onClick={() => switchMode('forgot')}
                className="text-xs text-rose-500 cursor-pointer whitespace-nowrap">
                忘记密码？
              </button>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-lock-line text-gray-400 text-base" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
                className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer">
                <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 text-base`} />
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-4 py-3 rounded-2xl">
              <i className="ri-error-warning-line text-base flex-shrink-0" /><span>{error}</span>
            </div>
          )}

          {/* 记住我 */}
          <button
            type="button"
            onClick={() => setRememberMe(!rememberMe)}
            className="flex items-center gap-2 cursor-pointer mt-1 w-fit"
          >
            <div className={`w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all ${rememberMe ? 'bg-rose-500 border-rose-500' : 'border-gray-300 bg-white'}`}>
              {rememberMe && <i className="ri-check-line text-white text-xs" />}
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">记住我（7天免登录）</span>
          </button>

          <div className="flex items-start gap-2.5">
            <button type="button" onClick={() => setAgreed(!agreed)}
              className={`w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all mt-0.5 ${agreed ? 'bg-rose-500 border-rose-500' : 'border-gray-300 bg-white'}`}>
              {agreed && <i className="ri-check-line text-white text-xs" />}
            </button>
            <p className="text-xs text-gray-500 leading-relaxed">
              我已阅读并同意<span className="text-rose-500 cursor-pointer">《用户协议》</span>和<span className="text-rose-500 cursor-pointer">《隐私政策》</span>
            </p>
          </div>

          <button type="submit" disabled={submitting}
            className="w-full bg-gradient-to-r from-rose-500 to-orange-400 text-white py-4 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap mt-2 disabled:opacity-60 transition-opacity">
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />登录中...
              </span>
            ) : '立即登录'}
          </button>
        </form>
      )}

      {/* ── 注册成功 ── */}
      {registerSuccess && (
        <div className="px-6 flex-1 flex flex-col items-center justify-center gap-6 pb-16">
          <div className="relative w-24 h-24 flex items-center justify-center">
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 96 96">
              <circle cx="48" cy="48" r="44" fill="none" stroke="#f0fdf4" strokeWidth="6" />
              <circle
                cx="48" cy="48" r="44" fill="none" stroke="#22c55e" strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 44}`}
                strokeDashoffset={`${2 * Math.PI * 44 * (1 - countdown / 3)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.9s linear' }}
              />
            </svg>
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-green-500 text-3xl" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-gray-900 mb-2">注册成功！</p>
            <p className="text-sm text-gray-400">
              <span className="text-green-500 font-semibold">{countdown}</span> 秒后自动前往登录
            </p>
          </div>
          <button
            onClick={() => { setRegisterSuccess(false); switchMode('login'); }}
            className="w-full bg-gradient-to-r from-rose-500 to-orange-400 text-white py-4 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap"
          >
            立即前往登录
          </button>
        </div>
      )}

      {mode === 'register' && !registerSuccess && (
        <form onSubmit={handleRegister} className="px-6 flex-1 flex flex-col gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">昵称 <span className="text-rose-500">*</span></label>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-user-smile-line text-gray-400 text-base" /></div>
              <input type="text" value={regNickname} onChange={(e) => setRegNickname(e.target.value)} placeholder="请输入昵称（展示用）"
                className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">用户名 <span className="text-rose-500">*</span></label>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-user-line text-gray-400 text-base" /></div>
              <input type="text" value={regUsername} onChange={(e) => setRegUsername(e.target.value)} placeholder="2-20位，支持中英文/数字/下划线"
                className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">手机号 <span className="text-rose-500">*</span></label>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-smartphone-line text-gray-400 text-base" /></div>
              <input type="tel" value={regPhone} onChange={(e) => setRegPhone(e.target.value)} placeholder="请输入手机号" maxLength={11}
                className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">密码 <span className="text-rose-500">*</span></label>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-lock-line text-gray-400 text-base" /></div>
              <input type={showRegPassword ? 'text' : 'password'} value={regPassword} onChange={(e) => setRegPassword(e.target.value)} placeholder="请设置密码（至少6位）"
                className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400" />
              <button type="button" onClick={() => setShowRegPassword(!showRegPassword)} className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer">
                <i className={`${showRegPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 text-base`} />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">确认密码 <span className="text-rose-500">*</span></label>
            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-lock-2-line text-gray-400 text-base" /></div>
              <input type={showRegPassword ? 'text' : 'password'} value={regConfirmPassword} onChange={(e) => setRegConfirmPassword(e.target.value)} placeholder="请再次输入密码"
                className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1.5 font-medium">
              邀请码
              {inviteCodeLocked
                ? <span className="ml-1.5 text-green-500 font-normal">（已自动填入）</span>
                : <span className="text-gray-400 font-normal">（选填）</span>
              }
            </label>
            <div className={`flex items-center gap-3 rounded-2xl px-4 py-3.5 ${inviteCodeLocked ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`ri-gift-line text-base ${inviteCodeLocked ? 'text-green-500' : 'text-gray-400'}`} />
              </div>
              <input
                type="text"
                value={regInviteCode}
                onChange={(e) => !inviteCodeLocked && setRegInviteCode(e.target.value.toUpperCase())}
                placeholder="填写邀请人的邀请码"
                readOnly={inviteCodeLocked}
                className={`flex-1 bg-transparent text-sm outline-none placeholder-gray-400 uppercase ${inviteCodeLocked ? 'text-green-600 font-medium cursor-not-allowed' : 'text-gray-800'}`}
                maxLength={10}
              />
              {inviteCodeLocked && (
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-fill text-green-400 text-sm" />
                </div>
              )}
            </div>
            {inviteCodeLocked && (
              <p className="text-xs text-green-500 mt-1 pl-1">邀请码已通过分享链接自动填入</p>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-4 py-3 rounded-2xl">
              <i className="ri-error-warning-line text-base flex-shrink-0" /><span>{error}</span>
            </div>
          )}

          <div className="flex items-start gap-2.5 mt-1">
            <button type="button" onClick={() => setAgreed(!agreed)}
              className={`w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all mt-0.5 ${agreed ? 'bg-rose-500 border-rose-500' : 'border-gray-300 bg-white'}`}>
              {agreed && <i className="ri-check-line text-white text-xs" />}
            </button>
            <p className="text-xs text-gray-500 leading-relaxed">
              我已阅读并同意<span className="text-rose-500 cursor-pointer">《用户协议》</span>和<span className="text-rose-500 cursor-pointer">《隐私政策》</span>
            </p>
          </div>

          <button type="submit" disabled={submitting}
            className="w-full bg-gradient-to-r from-rose-500 to-orange-400 text-white py-4 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap mt-2 disabled:opacity-60 transition-opacity">
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />注册中...
              </span>
            ) : '立即注册'}
          </button>
          <p className="text-center text-xs text-gray-400 pb-4">手机号+密码即可注册，简单快捷</p>
        </form>
      )}

      {/* ── 忘记密码 ── */}
      {mode === 'forgot' && (
        <div className="px-6 flex-1 flex flex-col">
          {forgotStep === 'input' && (
            <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
              <div className="bg-amber-50 rounded-2xl px-4 py-3 mb-2">
                <div className="flex items-start gap-2">
                  <i className="ri-information-line text-amber-500 text-sm mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700 leading-relaxed">输入注册时使用的手机号和新密码，即可直接重置登录密码</p>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">注册手机号 <span className="text-rose-500">*</span></label>
                <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-smartphone-line text-gray-400 text-base" />
                  </div>
                  <input
                    type="tel"
                    value={forgotPhone}
                    onChange={(e) => { setForgotPhone(e.target.value); setError(''); }}
                    placeholder="请输入注册时使用的手机号"
                    maxLength={11}
                    className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">新密码 <span className="text-rose-500">*</span></label>
                <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-lock-line text-gray-400 text-base" /></div>
                  <input
                    type={showForgotPwd ? 'text' : 'password'}
                    value={forgotNewPwd}
                    onChange={(e) => { setForgotNewPwd(e.target.value); setError(''); }}
                    placeholder="请设置新密码（至少6位）"
                    className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400"
                  />
                  <button type="button" onClick={() => setShowForgotPwd(!showForgotPwd)} className="w-5 h-5 flex items-center justify-center flex-shrink-0 cursor-pointer">
                    <i className={`${showForgotPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 text-base`} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">确认新密码 <span className="text-rose-500">*</span></label>
                <div className="flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3.5">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-lock-2-line text-gray-400 text-base" /></div>
                  <input
                    type={showForgotPwd ? 'text' : 'password'}
                    value={forgotConfirmPwd}
                    onChange={(e) => { setForgotConfirmPwd(e.target.value); setError(''); }}
                    placeholder="请再次输入新密码"
                    className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-4 py-3 rounded-2xl">
                  <i className="ri-error-warning-line text-base flex-shrink-0" /><span>{error}</span>
                </div>
              )}

              <button type="submit" disabled={submitting}
                className="w-full bg-gradient-to-r from-rose-500 to-orange-400 text-white py-4 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap mt-2 disabled:opacity-60 transition-opacity">
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />重置中...
                  </span>
                ) : '确认重置密码'}
              </button>
            </form>
          )}

          {forgotStep === 'done' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-6 pb-16">
              <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl" />
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-gray-900 mb-2">密码重置成功！</p>
                <p className="text-sm text-gray-400">请使用新密码重新登录</p>
              </div>
              <button onClick={() => switchMode('login')}
                className="w-full bg-gradient-to-r from-rose-500 to-orange-400 text-white py-4 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap">
                去登录
              </button>
            </div>
          )}
        </div>
      )}

      <div className="h-12" />

      {/* 冻结账号提示弹窗 */}
      {frozenNotice && (
        <div className="fixed inset-0 z-50 flex items-end justify-center pb-8 px-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setFrozenNotice(false)}
          />
          <div className="relative w-full max-w-[440px] bg-white rounded-3xl overflow-hidden animate-[slideUp_0.3s_ease-out]">
            {/* 顶部色条 */}
            <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 to-orange-400" />
            <div className="px-6 pt-6 pb-7">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-11 h-11 flex-shrink-0 rounded-2xl bg-amber-50 flex items-center justify-center">
                  <i className="ri-shield-keyhole-line text-amber-500 text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold text-gray-900 mb-1">账号处于特殊状态</p>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    您的账号当前处于特殊管理状态，登录和消费功能不受影响，但部分权益暂时受限。
                  </p>
                </div>
              </div>
              <div className="bg-amber-50 rounded-2xl px-4 py-3 mb-5">
                <p className="text-xs text-amber-700 leading-relaxed">
                  如有疑问，请联系客服或管理员了解详情。
                </p>
              </div>
              <button
                onClick={() => setFrozenNotice(false)}
                className="w-full bg-gradient-to-r from-amber-400 to-orange-400 text-white py-3.5 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap"
              >
                我知道了，继续使用
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
